import { ImageCard } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Button, SxProps, Theme } from '@mui/material';

import { useAgentContext } from './CategoryCard.agentContext';
import { SubCategoryProgressGroup } from '../atoms/SubCategoryProgressGroup';
import { CategoryProgress } from '../utils/transformScoresToProgressBar';

const LIFE_READINESS_STRINGS = appStrings.modules.lifeReadiness;

const FIGMA_CARD_HEIGHT = 315;
const FIGMA_CARD_WIDTH = 234;
const FIGMA_IMAGE_CONTAINER_HEIGHT = 100;

export type LifeReadinessCategoryCardProps = Readonly<{
  title: string;
  imageSrc: string;
  description: string;
  progress: CategoryProgress;
  onClick?: () => void;
  showCta?: boolean;
}>;

export const CategoryCard = (props: LifeReadinessCategoryCardProps) => {
  const { title, description, imageSrc, progress, onClick, showCta = false } = props;
  const cardId = `life-readiness-category-${title.toLowerCase().replace(/\s+/g, '-')}-card`;

  useAgentContext(props);

  if (progress.kind === 'complete') {
    return (
      <ImageCard
        id={cardId}
        imageUrl={imageSrc}
        title={title}
        onClick={onClick}
        sxOverrides={{
          container: onClick ? containerSx : staticContainerSx,
          image: imageSx,
          imageWrapper: imageWrapperSx,
          titleDescChildContainer: titleDescChildContainerWithProgressSx,
        }}
      >
        <SubCategoryProgressGroup progressBars={progress.progressBars} />
      </ImageCard>
    );
  }

  const ctaLabel =
    progress.kind === 'partial' ? LIFE_READINESS_STRINGS.ctaContinue : LIFE_READINESS_STRINGS.cta;

  return (
    <ImageCard
      imageUrl={imageSrc}
      title={title}
      id={cardId}
      onClick={onClick}
      description={description}
      sxOverrides={{
        container: onClick ? containerSx : staticContainerSx,
        imageWrapper: imageWrapperSx,
        image: imageSx,
        titleDescChildContainer: titleDescChildContainerNoProgressSx,
      }}
    >
      {onClick && showCta && (
        <Button
          id={`life-readiness-category-${title.toLowerCase().replace(/\s+/g, '-')}-button`}
          variant="contained"
          onClick={onClick}
          hierarchy="Primary"
          size="lg"
          fullWidth
          sx={buttonSx}
        >
          {ctaLabel}
        </Button>
      )}
    </ImageCard>
  );
};

const buttonSx: SxProps<Theme> = ({ backrs }) => ({
  marginTop: backrs.spacing['spacing-xl'],
});

const containerSx: SxProps<Theme> = {
  width: '100% !important',
  maxWidth: '100% !important',
  minWidth: `${FIGMA_CARD_WIDTH}px`,
  minHeight: `${FIGMA_CARD_HEIGHT}px`,
  height: '100%',
};

// ImageCard styles every card as clickable, so a card with nowhere to go undoes it.
const staticContainerSx: SxProps<Theme> = {
  ...containerSx,
  cursor: 'default',
  '&:hover': { boxShadow: 'none', transform: 'none' },
};

const imageWrapperSx: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Foreground/fg-secondary'],
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: { xs: '100%' },
  minHeight: `${FIGMA_IMAGE_CONTAINER_HEIGHT}px`,
  height: `${FIGMA_IMAGE_CONTAINER_HEIGHT}px`,
  overflow: 'hidden',
});

const imageSx: SxProps<Theme> = {
  height: '100%',
  width: '100%',
  objectFit: 'cover',
};

const titleDescChildContainerNoProgressSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
};

const titleDescChildContainerWithProgressSx: SxProps<Theme> = {
  height: '100%',
};
